import { useState, useEffect, useContext } from 'react';
import { Modal, Container, Row, Col, Button, Form } from 'react-bootstrap';
import type { IIncome } from '../../../interfaces/IIncome';
import type { ILocation } from '../../../interfaces/ILocation';
import Common from '../../shared/Common';
import LocationsAutosuggest from '../../shared/locations-autosuggest/LocationsAutosuggest';
import WarningModal from '../../shared/shared-modals/WarningModal';
import { DictionaryContext } from '../../../App';
import type { IIncomeCategory } from '../../../interfaces/IIncomeCategory';
import type { IIncomeQuantityType } from '../../../interfaces/IIncomeQuantityType';

import './style.css';

export interface IIncomeModalProps {
  show: boolean;
  onHide: () => void;
  onIncomeAdd: () => void;
  onIncomeSave: (income: IIncome) => void;
  onIncomeRemove: (incomeId: number) => void;
  fetchIncome: (incomeId: number) => Promise<void>;
  income: IIncome;
  setIncome: React.Dispatch<React.SetStateAction<IIncome>>;
  incomeId: number;
  incomeQuantityTypes: IIncomeQuantityType[];
  incomeCategories: IIncomeCategory[];
  locationNames: ILocation[];
  copyMode: boolean;
  setCopyMode: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  initialLoading: boolean;
}

enum IncomeWarningType {
  RemoveConfirmation = 0,
  PendingChanges = 1,
}

function IncomeModal(props: IIncomeModalProps) {
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [warningType, setWarningType] = useState<IncomeWarningType>(0);
  const [pendingChanges, setPendingChanges] = useState<boolean>(false);
  const [quantityString, setQunatityString] = useState<string>(props.income.quantity?.toString() ?? '');
  const [revenueString, setRevenueString] = useState<string>(props.income.revenue?.toString() ?? '');
  const { reloadLocationNames } = useContext(DictionaryContext);

  const quantityTypeItems: any[] = [];
  for (let ind = 0; ind !== (props.incomeQuantityTypes?.length ?? 0); ind++) {
    quantityTypeItems.push(
      <option
        key={props.incomeQuantityTypes[ind]?.incomeQuantityTypeId}
        value={JSON.stringify(props.incomeQuantityTypes[ind])}
      >
        {props.incomeQuantityTypes[ind]?.incomeQuantityTypeName ?? ''}
      </option>
    );
  }
  
  const categoryItems: any[] = [];
  for (let ind = 0; ind !== (props.incomeCategories?.length ?? 0); ind++) {
    categoryItems.push(
      <option key={props.incomeCategories[ind]?.incomeCategoryId} value={JSON.stringify(props.incomeCategories[ind])}>
        {props.incomeCategories[ind]?.incomeCategoryName ?? ''}
      </option>
    );
  }

  const showRemoveIncomeWarningModal = () => {
    setWarningType(IncomeWarningType.RemoveConfirmation);
    setShowWarningModal(true);
  };

  const showClosePendingChangesWarningModal = () => {
    setWarningType(IncomeWarningType.PendingChanges);
    setShowWarningModal(true);
  };

  const { fetchIncome, incomeId, income, setIncome } = props;

  useEffect(() => {
    setRevenueString(income.revenue?.toString() ?? '');
    setQunatityString(income.quantity?.toString() ?? '');
  }, [income.quantity, income.revenue])

  useEffect(() => {
    fetchIncome(incomeId);
    return () => {
      setIncome({
        incomeId: 0,
        location: {
          locationId: -1,
          locationName: '',
          picture: '',
          mappings: [],
        },
        category: props.incomeCategories.length > 0 ? props.incomeCategories[0] : undefined,
        quantityType: {
          incomeQuantityTypeId: 1,
          incomeQuantityTypeShortName: 'szt.',
          incomeQuantityTypeName: 'Sztuk',
        },
        description: '',
        revenue: 0,
        quantity: 1,
        time: new Date(),
      });
    };
  }, [setIncome, fetchIncome, incomeId, props.incomeCategories]);

  const saveIncome = async () => {
    props.setLoading(true);
    if (!props.copyMode && props.incomeId !== 0) {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...props.income }),
      };
      const response = await Common.authorizedFetch('api/incomes/saveIncome', requestOptions);
      const data = await response.json();
      if (!data.success) {
        alert(data.errors);
      } else {
        if (data.result.newLocationAdded && reloadLocationNames) {
          reloadLocationNames();
        }
        props.onIncomeSave(props.income);
      }
    } else {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...props.income }),
      };
      const response = await Common.authorizedFetch('api/incomes/addIncome', requestOptions);
      const data = await response.json();
      if (!data.success) {
        alert(data.errors);
      } else {
        if (data.result.newLocationAdded && reloadLocationNames) {
          reloadLocationNames();
        }
        props.onIncomeAdd();
      }
    }
    setPendingChanges(false);
    props.onHide();
  };

  const removeIncome = async () => {
    props.setLoading(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ incomeId: props.income.incomeId }),
    };
    const response = await Common.authorizedFetch('api/incomes/removeIncome', requestOptions);
    const data = await response.json();
    if (!data.success) {
      alert(data.errors);
    } else {
      props.onIncomeRemove(props.income.incomeId);
    }
    setShowWarningModal(false);
    setPendingChanges(false);
    props.onHide();
  };
  
  return (
    <>
      <Modal
        show={props.show}
        onHide={() => {
          if (pendingChanges) {
            showClosePendingChangesWarningModal();
          } else {
            setPendingChanges(false);
            props.onHide();
          }
        }}
        aria-labelledby='contained-modal-title-vcenter'
        style={{ maxWidth: '100%', overflowX: 'auto', ...(showWarningModal ? { zIndex: 1049 } : {}) }}
      >
        <Modal.Header closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>
            {props.incomeId === 0 || props.copyMode ? 'Dodaj nowy przychód' : 'Przychód'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Common.Ui.showLoadingSpinnerFixed(props.loading)}
          <Form>
            <Container>
              <Row>
                <Col>
                  <Form.Group className='mb-3' controlId='locationName'>
                    <Form.Label>Lokalizacja</Form.Label>
                    <LocationsAutosuggest
                      key={props.income.location?.locationId}
                      locations={props.locationNames}
                      selectedValue={props.income.location}
                      onChange={(location: ILocation) => {
                        setPendingChanges(true);
                        props.setIncome((prevState) => {
                          const newState = {
                            ...prevState,
                            location: location,
                          };
                          return newState;
                        });
                      }}
                      inputClassName='form-control form-control-sm'
                      showForIncome={true}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className='mb-3' controlId='date'>
                    <Form.Label>Data</Form.Label>
                    <Form.Control
                      className='form-control form-control-sm'
                      type='date'
                      name='time'
                      placeholder='Data zakupu'
                      onChange={(e: any) => {
                        const date = e.target.value;
                        setPendingChanges(true);
                        const updatedDate = new Date(date);
                        props.setIncome((prevState) => {
                          const newState = {
                            ...prevState,
                            time: updatedDate,
                          };
                          return newState;
                        });
                      }}
                      value={
                        !props.income.time
                          ? new Date().toLocaleString('en-CA').split(',')[0]
                          : new Date(props.income.time).toLocaleString('en-CA').split(',')[0]
                      }
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className='mb-3' controlId='category'>
                    <Form.Label>Kategoria</Form.Label>
                    <Form.Control
                      className='form-control form-control-sm'
                      as='select'
                      value={JSON.stringify(income.category)}
                      onChange={(e: any) => {
                        setPendingChanges(true);
                        props.setIncome((prevState) => {
                          const category = JSON.parse(e.target.value);
                          const newState = {
                            ...prevState,
                            category: category,
                          };
                          return newState;
                        });
                      }}
                    >
                      {categoryItems}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className='mb-3' controlId='description'>
                    <Form.Label>Opis</Form.Label>
                    <Form.Control
                      className='form-control form-control-sm'
                      type='text'
                      onChange={(e: any) => {
                        setPendingChanges(true);
                        props.setIncome((prevState) => {
                          const newState = {
                            ...prevState,
                            description: e.target.value,
                          };
                          return newState;
                        });
                      }}
                      value={income.description}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className='mb-3' controlId='revenue'>
                    <Form.Label>Kwota (zł)</Form.Label>
                    <Form.Control
                      className='form-control form-control-sm'
                      value={revenueString}
                      type='text'
                      onChange={(e: any) => {
                        setPendingChanges(true);
                        setRevenueString(e.target.value);
                      }}
                      onBlur={(e: any) => {
                        let stringValue = String(revenueString);
                        if (!stringValue) {
                          stringValue = '0.00';
                        }
                        stringValue = stringValue.replace(',', '.');
                        const numberValue = parseFloat(stringValue);
                        setRevenueString(numberValue.toString());
                        props.setIncome((prevState) => {
                          const newState = {
                            ...prevState,
                            revenue: numberValue,
                          };
                          return newState;
                        });
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col style={{maxWidth: '80px'}}>
                  <Form.Group className='mb-3' controlId='quantity'>
                    <Form.Label>Ile</Form.Label>
                    <Form.Control
                      className='form-control form-control-sm'
                      type='text'
                      onChange={(e: any) => {
                        setPendingChanges(true);
                        setQunatityString(e.target.value);
                      }}
                      onBlur={(e: any) => {
                        let stringValue = String(quantityString);
                        if (!stringValue) {
                          stringValue = '0.00';
                        }
                        stringValue = stringValue.replace(',', '.');
                        const numberValue = parseFloat(stringValue);
                        setQunatityString(numberValue.toString());
                        props.setIncome((prevState) => {
                          const newState = {
                            ...prevState,
                            quantity: numberValue,
                          };
                          return newState;
                        });
                      }}
                      value={quantityString}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className='mb-3' controlId='quantityType'>
                    <Form.Label>Jednostka</Form.Label>
                    <Form.Control
                      className='form-control form-control-sm'
                      as='select'
                      value={JSON.stringify(income.quantityType)}
                      onChange={(e: any) => {
                        setPendingChanges(true);
                        props.setIncome((prevState) => {
                          const quantityType = JSON.parse(e.target.value);
                          const newState = {
                            ...prevState,
                            quantityType: quantityType,
                          };
                          return newState;
                        });
                      }}
                    >
                      {quantityTypeItems}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
            </Container>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div className='me-sm-auto' />
          <Button
            variant='dark'
            onClick={() => {
              if (pendingChanges) {
                showClosePendingChangesWarningModal();
              } else {
                setPendingChanges(false);
                props.onHide();
              }
            }}
          >
            Zamknij
          </Button>
          <Button
            variant='dark'
            disabled={props.loading || props.initialLoading || props.incomeId === 0 || props.copyMode}
            onClick={() => {
              props.setCopyMode(true);
              props.setIncome((prevState) => ({
                ...prevState,
                time: new Date(),
              }));
            }}
          >
            Kopiuj
          </Button>
          <Button
            variant='danger'
            disabled={props.loading || props.initialLoading || props.incomeId === 0 || props.copyMode}
            onClick={showRemoveIncomeWarningModal}
          >
            Usuń
          </Button>
          <Button
            variant='success'
            disabled={props.loading || props.initialLoading}
            onClick={() => {
              saveIncome();
            }}
          >
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
      <WarningModal
        show={showWarningModal}
        modalBodyText={(() => {
          switch (warningType) {
            case IncomeWarningType.RemoveConfirmation:
              return 'Czy na pewno chcesz usunąć ten przychód?';
            case IncomeWarningType.PendingChanges:
              return 'Wprowadzone zmiany w przychodzie nie zostały zapisane. Czy na pewno chcesz kontynuować?';
            default:
              return '';
          }
        })()}
        onHide={() => {
          setShowWarningModal(false);
          setPendingChanges(false);
        }}
        onConfirmation={() => {
          switch (warningType) {
            case IncomeWarningType.RemoveConfirmation:
              removeIncome();
              return;
            case IncomeWarningType.PendingChanges:
              setShowWarningModal(false);
              setPendingChanges(false);
              props.onHide();
              return;
          }
        }}
      />
    </>
  );
}

export default IncomeModal;
